import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Timeline } from "primereact/timeline";
import React from "react";

const TaskDetails = ({ task }) => {
  if (!task) {
    return <div>No task selected</div>;
  }

  console.log("Task details:", task);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format date with time for display
  const formatDateWithTime = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Calculate duration in days
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Reset time parts to ensure accurate day calculation
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "to do":
        return "#6c757d";
      case "in progress":
        return "#007bff";
      case "review":
        return "#ffc107";
      case "done":
        return "#28a745";
      case "pause":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "to do":
        return "pi pi-list";
      case "in progress":
        return "pi pi-spin pi-spinner";
      case "review":
        return "pi pi-check-square";
      case "done":
        return "pi pi-check-circle";
      default:
        return "pi pi-question-circle";
    }
  };

  // Get random color for avatar based on name
  const getAvatarColor = (name) => {
    if (!name) return "var(--primary-color)";
    const colors = [
      "var(--blue-500)",
      "var(--green-500)",
      "var(--yellow-500)",
      "var(--cyan-500)",
      "var(--pink-500)",
      "var(--indigo-500)",
      "var(--teal-500)",
      "var(--orange-500)",
      "var(--purple-500)",
    ];
    const hashCode = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hashCode % colors.length];
  };

  // Get initials from name for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Timeline events for task history
  const timelineEvents = [
    {
      status: "Planned",
      date: task.start_date,
      icon: "pi pi-calendar",
      color: "var(--blue-500)",
    },
  ];

  if (task.actual_start_date) {
    timelineEvents.push({
      status: "Started",
      date: task.actual_start_date,
      icon: "pi pi-play",
      color: "var(--green-500)",
    });
  }

  if (task.actual_end_date) {
    timelineEvents.push({
      status: "Completed",
      date: task.actual_end_date,
      icon: "pi pi-check",
      color: "var(--teal-500)",
    });
  } else if (task.end_date) {
    timelineEvents.push({
      status: "Planned End",
      date: task.end_date,
      icon: "pi pi-flag",
      color: "var(--orange-500)",
    });
  }

  // Sort timeline events by date
  timelineEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Render HTML content safely
  const renderHTML = (htmlContent) => {
    if (!htmlContent) return "No description provided.";
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  return (
    <div className="task-details-modern">
      {/* Task Header with Status and Meta */}
      <div className="task-header-modern">
        <div className="header-left">
          <div className="status-indicator" style={{ backgroundColor: getStatusColor(task.status) }}>
            <i className={getStatusIcon(task.status)}></i>
          </div>
          <div className="header-text">
            <h2 className="task-title">{task.title}</h2>
            <div className="task-meta">
              {task.project && (
                <div className="meta-item">
                  <i className="pi pi-folder meta-icon"></i>
                  <span>{task.project.title}</span>
                </div>
              )}
              {task.taskType && (
                <div className="meta-item">
                  <i className="pi pi-tag meta-icon"></i>
                  <span>{task.taskType.task_type}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <Tag value={task.status} className="status-tag" style={{ backgroundColor: getStatusColor(task.status) }} />
      </div>

      {/* Main Content Area */}
      <div className="task-content">
        <div className="task-left-panel">
          {/* Description Panel */}
          <div className="content-panel description-panel">
            <div className="panel-header">
              <i className="pi pi-info-circle panel-icon"></i>
              <h3>Description</h3>
            </div>
            <div className="description-content">{renderHTML(task.description)}</div>
          </div>

          {/* Duration Panel */}
          <div className="content-panel duration-panel">
            <div className="duration-grid">
              <div className="duration-cell">
                <div className="cell-label">Start Date</div>
                <div className="cell-value">{formatDate(task.start_date)}</div>
              </div>
              <div className="duration-cell">
                <div className="cell-label">Due Date</div>
                <div className="cell-value">{formatDate(task.end_date)}</div>
              </div>
              <div className="duration-cell">
                <div className="cell-label">Planned Duration</div>
                <div className="cell-value">{calculateDuration(task.start_date, task.end_date)}</div>
              </div>
              {task.actual_start_date && (
                <>
                  <div className="duration-cell">
                    <div className="cell-label">Actual Start</div>
                    <div className="cell-value highlight">{formatDate(task.actual_start_date)}</div>
                  </div>
                  <div className="duration-cell">
                    <div className="cell-label">Actual End</div>
                    <div className="cell-value highlight">{task.actual_end_date ? formatDate(task.actual_end_date) : "In progress"}</div>
                  </div>
                  <div className="duration-cell">
                    <div className="cell-label">Actual Duration</div>
                    <div className="cell-value highlight">
                      {task.actual_end_date ? calculateDuration(task.actual_start_date, task.actual_end_date) : "In progress"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Developers Panel */}
          <div className="content-panel developers-panel">
            <div className="panel-header">
              <i className="pi pi-users panel-icon"></i>
              <h3>Assigned Developers</h3>
            </div>

            {task.developers && task.developers.length > 0 ? (
              <div className="developers-grid">
                {task.developers.map((dev) => {
                  const fullName = dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `Developer #${dev.id}`;
                  const bgColor = getAvatarColor(fullName);

                  return (
                    <div key={dev.id} className="developer-card">
                      <Avatar
                        label={getInitials(fullName)}
                        shape="circle"
                        style={{ backgroundColor: bgColor, color: "#ffffff" }}
                        className="developer-avatar"
                      />
                      <div className="developer-info">
                        <div className="developer-name">{fullName}</div>
                        {dev.role && <div className="developer-role">{dev.role}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-developers">No developers assigned</p>
            )}
          </div>
        </div>

        <div className="task-right-panel">
          {/* Status Timeline Panel */}
          <div className="content-panel timeline-panel">
            <div className="panel-header">
              <i className="pi pi-clock panel-icon"></i>
              <h3>Timeline</h3>
            </div>

            <Timeline
              value={timelineEvents}
              className="custom-timeline"
              content={(item) => (
                <div className="timeline-event">
                  <div className="timeline-event-title">{item.status}</div>
                  <div className="timeline-event-time">{formatDateWithTime(item.date)}</div>
                </div>
              )}
              marker={(item) => (
                <span
                  className="timeline-marker"
                  style={{
                    backgroundColor: item.color,
                  }}
                >
                  <i className={item.icon}></i>
                </span>
              )}
            />

            {/* Status History Section */}
            <div className="panel-header status-history-header">
              <i className="pi pi-history panel-icon"></i>
              <h3>Status History</h3>
            </div>

            {task.status_history && task.status_history.length > 0 ? (
              <div className="status-flow">
                {task.status_history.map((status, index) => (
                  <div key={status.id} className="status-card">
                    <div className="status-transition">
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(status.old_status || "Created") }}>
                        {status.old_status || "Created"}
                      </span>
                      <i className="pi pi-arrow-right status-arrow"></i>
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(status.new_status) }}>
                        {status.new_status}
                      </span>
                      {index === 0 && <span className="latest-badge">Latest</span>}
                    </div>
                    <div className="status-details">
                      <div className="status-time">
                        <i className="pi pi-calendar-plus time-icon"></i>
                        {formatDateWithTime(status.changed_at)}
                      </div>
                      {status.comment && <div className="status-comment">{status.comment}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-status">
                <i className="pi pi-info-circle empty-icon"></i>
                <p>No status history available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task Action Buttons */}
      <div className="task-actions-modern">
        <Button icon="pi pi-pencil" label="Edit Task" className="p-button-outlined p-button-primary" />
        <Button
          icon="pi pi-check-circle"
          label={task.status === "Done" ? "Reopen Task" : "Mark as Complete"}
          className={task.status === "Done" ? "p-button-outlined p-button-warning" : "p-button-success"}
        />
      </div>

      <style>{`
        .task-details-modern {
          display: flex;
          flex-direction: column;
          height: 100%;
          background-color: var(--surface-ground);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        }
        
        /* Modern Header */
        .task-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background-color: var(--surface-card);
          border-bottom: 1px solid var(--surface-border);
        }
        
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .status-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          color: white;
        }
        
        .header-text {
          display: flex;
          flex-direction: column;
        }
        
        .task-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-color);
        }
        
        .task-meta {
          display: flex;
          gap: 1rem;
          margin-top: 0.25rem;
        }
        
        .meta-item {
          display: flex;
          align-items: center;
          font-size: 0.8rem;
          color: var(--text-color-secondary);
        }
        
        .meta-icon {
          margin-right: 0.35rem;
          font-size: 0.85rem;
        }
        
        .status-tag {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.35rem 0.8rem;
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        /* Main Content */
        .task-content {
          display: flex;
          flex: 1;
          overflow: hidden;
          padding: 1rem;
          gap: 1rem;
        }
        
        .task-left-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          overflow-y: auto;
          max-width: 60%;
        }
        
        .task-right-panel {
          flex: 1;
          overflow-y: auto;
          max-width: 40%;
        }
        
        /* Content Panels */
        .content-panel {
          background-color: var(--surface-card);
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }
        
        .panel-header {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--surface-border);
          background-color: var(--surface-section);
        }
        
        .panel-icon {
          margin-right: 0.5rem;
          color: var(--primary-color);
          font-size: 0.9rem;
        }
        
        .panel-header h3 {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-color);
        }
        
        /* Description Panel */
        .description-panel {
          flex: 1;
        }
        
        .description-content {
          padding: 1rem;
          color: var(--text-color-secondary);
          font-size: 0.9rem;
          line-height: 1.5;
          max-height: 200px;
          overflow-y: auto;
        }
        
        /* Duration Panel */
        .duration-panel {
          margin-bottom: 1rem;
        }
        
        .duration-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          padding: 0.75rem;
        }
        
        .duration-cell {
          padding: 0.5rem;
          border-radius: 6px;
          background-color: var(--surface-hover);
        }
        
        .cell-label {
          font-size: 0.7rem;
          color: var(--text-color-secondary);
          margin-bottom: 0.25rem;
        }
        
        .cell-value {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--text-color);
        }
        
        .cell-value.highlight {
          color: var(--primary-color);
        }
        
        /* Developers Panel */
        .developers-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 0.75rem;
        }
        
        .developer-card {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          border-radius: 6px;
          background-color: var(--surface-hover);
          width: calc(50% - 0.25rem);
        }
        
        .developer-avatar {
          width: 2rem;
          height: 2rem;
          font-size: 0.85rem;
        }
        
        .developer-name {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--text-color);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .developer-role {
          font-size: 0.75rem;
          color: var(--text-color-secondary);
        }
        
        .no-developers {
          padding: 1rem;
          text-align: center;
          color: var(--text-color-secondary);
          font-style: italic;
          font-size: 0.9rem;
        }
        
        /* Timeline Panel */
        .timeline-panel {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .custom-timeline {
          padding: 1rem;
          flex: 0 0 auto;
        }
        
        .custom-timeline .p-timeline-event-opposite {
          display: none;
        }
        
        .timeline-event {
          padding: 0.35rem 0;
        }
        
        .timeline-event-title {
          font-weight: 600;
          color: var(--text-color);
          font-size: 0.85rem;
        }
        
        .timeline-event-time {
          color: var(--text-color-secondary);
          font-size: 0.8rem;
        }
        
        .timeline-marker {
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.7rem;
        }
        
        /* Status History */
        .status-history-header {
          margin-top: 0.5rem;
          border-top: 1px solid var(--surface-border);
        }
        
        .status-flow {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 0.75rem;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .status-card {
          background-color: var(--surface-hover);
          border-radius: 6px;
          padding: 0.75rem;
        }
        
        .status-transition {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        
        .status-badge {
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          color: white;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .status-arrow {
          color: var(--text-color-secondary);
          font-size: 0.8rem;
        }
        
        .latest-badge {
          margin-left: auto;
          background-color: var(--primary-color);
          color: white;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.15rem 0.4rem;
          border-radius: 3px;
        }
        
        .status-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .status-time {
          display: flex;
          align-items: center;
          font-size: 0.75rem;
          color: var(--text-color-secondary);
        }
        
        .time-icon {
          margin-right: 0.35rem;
          font-size: 0.8rem;
          color: var(--primary-color);
        }
        
        .status-comment {
          font-size: 0.8rem;
          color: var(--text-color);
          background-color: rgba(0, 0, 0, 0.03);
          padding: 0.4rem 0.6rem;
          border-radius: 4px;
          line-height: 1.4;
        }
        
        .empty-status {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1.5rem;
          color: var(--text-color-secondary);
        }
        
        .empty-icon {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          opacity: 0.6;
        }
        
        /* Action Buttons */
        .task-actions-modern {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background-color: var(--surface-card);
          border-top: 1px solid var(--surface-border);
        }
        
        /* Media Queries */
        @media (max-width: 992px) {
          .task-content {
            flex-direction: column;
          }
          
          .task-left-panel,
          .task-right-panel {
            max-width: 100%;
          }
          
          .duration-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 576px) {
          .duration-grid {
            grid-template-columns: 1fr;
          }
          
          .developer-card {
            width: 100%;
          }
          
          .task-header-modern {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
          
          .status-tag {
            align-self: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default TaskDetails;
